import React from 'react';
import { Term } from '@services';
import { FacetAccordion } from '@components';
import { Input, Label, Typography } from 'gd-design-library';
import styles from './FacetTerms.module.scss';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

const CN = 'facet-terms';
const termCountClassName = `${CN}__term-count`;
const termNameClassName = `${CN}__term-name`;
const labelClassName = `${CN}__label`;
const labelTextClassName = `${CN}__label-text`;

type TermChangeHandler = (term: string) => void

interface Props {
  label: string;
  termsType: string;
  terms: Term[];
  selectedTerms: string[];
  selectTerm: TermChangeHandler;
  deleteTerm: TermChangeHandler;
  countEntity?: 'variants' | 'products';
}

export const FacetTerms: React.FC<Props> = ({
  label,
  terms,
  termsType,
  selectedTerms,
  selectTerm,
  deleteTerm,
  countEntity = 'products'
}) => {
  const selectedTermSet = new Set(selectedTerms);

  // show a list of previously selected options which are not among current terms
  // so user has an ability to deselect them
  const presentTerms = new Set(terms.map(({ term }) => term));
  const selectedButNotPresentInTerms = selectedTerms.filter(term => !presentTerms.has(term));

  const noOptionsAvailable = !terms.length && !selectedButNotPresentInTerms.length;

  const getCheckHandler = (term: string) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      selectTerm(term);
    } else {
      deleteTerm(term);
    }
  };

  return (
    <FacetAccordion hasShowMoreButton={ terms.length >= 5 } label={ label }>
      <Typography>
        {selectedButNotPresentInTerms.map(item => (
          <Label key={ item } className={ cx(labelClassName) }>
            <Input
              role='checkbox'
              variant='checkbox'
              checked={ true }
              onChange={ getCheckHandler(item) }
            />
            <div className={ cx(labelTextClassName) }>
              {item} <span className={ cx(termCountClassName) }>(0)</span>
            </div>
          </Label>))}
        {terms.map(item => {
          let termValue: string | number = item.term;
          const termCount = countEntity === 'variants' ? item.count : item.productCount;

          if (termsType === 'number' && Number.isInteger(+item.term)) {
            termValue = parseInt(item.term);
          } else {
            termValue = item?.displayTerm || item.term;
          }

          return <Label key={ item.term } className={ cx(labelClassName) }>
            <Input
              role='checkbox'
              variant='checkbox'
              className={ cx(termNameClassName) }
              checked={ selectedTermSet.has(item.term) }
              onChange={ getCheckHandler(item.term) }
              disabled={ !item.count }
            />
            <div className={ cx(labelTextClassName) }>
              {termValue} <span className={ cx(termCountClassName) }>({termCount})</span>
            </div>
          </Label>;
        })}
        {noOptionsAvailable && (
          <Typography>No options available</Typography>
        )}
      </Typography>
    </FacetAccordion>
  );
};
